import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {ExhibitTypeData} from "types/ExhibitType";
import ExhibitTypeForm from "pages/ExhibitType/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "context/OperatorContext";
import NotAuthorised from "../../NotAuthorised";
import {useAuth} from "../../../context/AuthContext";

function MutateExhibitType() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();
  const {id} = useParams();

  const {data: exhibitType, isLoading} = useQuery(
    ['exhibit-types', {id}],
    () => ngage.exhibitTypes.get(Number(id)),
    {enabled: !!id && auth.can('exhibit-types:read')}
  )

  const store = useMutation((data: ExhibitTypeData) => ngage.exhibitTypes.store(data))
  const update = useMutation((data: ExhibitTypeData) => ngage.exhibitTypes.update(Number(id), data))

  // Handle form submission
  async function handleSubmit(values: ExhibitTypeData, {setSubmitting}: FormikHelpers<ExhibitTypeData>) {
    id ? await handleUpdate(values) : await handleStore(values)
    await invalidate('exhibit-types.all')

    setSubmitting(false)
    navigate('/exhibit-types')
  }

  // Handle store mutation
  async function handleStore(values: ExhibitTypeData) {
    try {
      await store.mutateAsync(values)
      notify("success", `${termFor('Exhibit Type')} Created`, `${values.name} has been created`)
    } catch (error) {
      notify("error", `${termFor('Exhibit Type')} Not Created`, `${values.name} could not be stored`)
      throw error
    }
  }

  // Handle update mutation
  async function handleUpdate(values: ExhibitTypeData) {
    try {
      await update.mutateAsync(values)
      notify("success", `${termFor('Exhibit Type')} Updated`, `${values.name} has been updated`)
      await invalidate(['exhibit-types', {id}])
    } catch (error) {
      notify("error", `${termFor('Exhibit Type')} Not Updated`, `${values.name} could not be updated`)
      throw error
    }
  }

  // Prevent unauthorised viewing
  if (auth.cannot('exhibit-types:write')) {
    return <NotAuthorised/>
  }

  // Prepare breadcrumbs
  const newBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Exhibit Types')]: '/exhibit-types',
    [`New ${termFor('Exhibit Type')}`]: '/exhibit-types/new'
  }
  const editBreadcrumbs = {
    'App Content': '/',
    'Site': '/',
    [termFor('Exhibit Types')]: '/exhibit-types',
    [`Edit ${termFor('Exhibit Type')}`]: `/exhibit-types/${id}/edit`
  }

  return (
    <Page
      title={`${id ? 'Edit' : 'New'} ${termFor('Exhibit Type')}`}
      breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
      isLoading={isLoading}
    >
      <ExhibitTypeForm exhibitType={exhibitType} onSubmit={handleSubmit}/>
    </Page>
  );
}

export default MutateExhibitType;